<template>
  <article v-if="newsPost" class="news-post-full">
    <div class="news-post-full__nav">
      <BackButton to="/aktuell" />
      <BackendLink :path />
    </div>
    <PageHeading
      :title="`${useFormatDate('fullDateShort', newsPost.date.value)} – ${newsPost.title}`"
      :date="newsPost.date.value"
      type="news"
    />
    <div class="news-post-full__intro">
      <div class="news-post-full__cover">
        <ResponsiveImage v-if="newsPost.images.length === 1" :image="newsPost.images[0]" />
        <Slideshow v-if="newsPost.images.length > 1" :images="newsPost.images" />
      </div>
      <Text :text="newsPost.lead" />
    </div>
    <PageContent>
      <Blocks v-if="newsPost.content?.list" :items="newsPost.content?.list" class="news-post-full__content" />
    </PageContent>
  </article>
</template>

<script setup lang="ts">
import type { UseSeoMetaInput } from '@unhead/schema'
import { type NewsPostFullFragment, type NewsPostQuery } from '#graphql-operations'

const props = defineProps<{
  path: string
}>()

const { data } = await useAsyncGraphqlQuery('newsPost', { path: props.path }, {
  transform: function (v) {
    return v.data as NewsPostQuery
  }
})

const route: EntityRoute | undefined | null = data.value?.route

await useHandleRedirect(route)

if (route?.entity === undefined) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found'
  })
}

const newsPost: NewsPostFullFragment = route?.entity

const description = useStripHTML(newsPost.lead ?? '')
const ogImageUrl = useImageFallback(newsPost?.images?.[0])

const metatags: UseSeoMetaInput = {
  title: newsPost.title,
  description,
  ogDescription: description
}

if (ogImageUrl.length > 0) {
  metatags.ogImageUrl = ogImageUrl
}

useSeoMeta(metatags)

</script>

<style lang="scss" scoped>
@use "~/assets/sass/tools";

.news-post-full {
  &__nav {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--space--xxl);
  }

  &__intro {
    @include tools.grid;
    margin-bottom: var(--grid-gutter);

    .text {
      @include tools.page-grid-columns;

      &>* {
        &:first-child { // stylelint-disable-line
          margin-top: 0;
        }

        &:last-child { // stylelint-disable-line
          margin-bottom: 0;
        }
      }
    }
  }

  &__cover {
    @include tools.page-grid-columns;
  }
}
</style>
